import { startOfMinute } from "date-fns/startOfMinute";
import { format } from "fecha";
import type { Extent } from "ol/extent";
import { getBottomLeft, getTopRight } from "ol/extent";

import type { SearchEpicentersParams } from "../../../../api/epicenter/search";
import { AbstractPanel } from "../../../../common/abstractconditionpanel";
import { allEqLocationTypeIds, eqLocationTypeIdToString } from "../../../../enums";
import hx from "../../../../utils/hydroxie";
import { pickElement } from "../../../../utils/pickelements";

/**
 * 震源検索→検索条件エリア→気象庁情報パネル
 */
export class JmaPanel extends AbstractPanel<SearchEpicentersParams> {
    /**
     * 初期化処理を行う。
     * 気象庁震源の検索条件UI要素を生成し、イベントリスナーを設定する。
     */
    async init() {
        const elem = this.getElem();

        elem.replaceChildren(...this.#createElement());

        elem.addEventListener("change", () => {
            const params = this.#getParams();
            this.emit("update", params);
        });
    }

    #createElement() {
        const jemOriginTimeTo = startOfMinute(new Date());
        const jemOriginTimeFrom = new Date("1996-01-01T00:00:00");
        const jemOriginTimeToStr = format(jemOriginTimeTo, "YYYY-MM-DDTHH:mm");
        const jemOriginTimeFromStr = format(jemOriginTimeFrom, "YYYY-MM-DDTHH:mm");

        return [
            hx("div", { className: "field origintime" },
                hx("label", { className: "label" }, "地震発生時刻"),
                hx("div", { className: "control horizontal" },
                    hx("input", { type: "datetime-local", className: "input is-small", name: "jemOriginTimeFrom", value: jemOriginTimeFromStr }),
                    hx("span", "〜"),
                    hx("input", { type: "datetime-local", className: "input is-small", name: "jemOriginTimeTo", value: jemOriginTimeToStr }))),

            hx("div", { className: "field" },
                hx("label", { className: "label" }, "震源緯度"),
                hx("div", { className: "control horizontal" },
                    hx("input", { type: "number", step: "any", min: "-90", max: "90", className: "input is-small", name: "jemLatFrom" }),
                    hx("span", "〜"),
                    hx("input", { type: "number", step: "any", min: "-90", max: "90", className: "input is-small", name: "jemLatTo" }))),

            hx("div", { className: "field" },
                hx("label", { className: "label" }, "震源経度"),
                hx("div", { className: "control horizontal" },
                    hx("input", { type: "number", step: "any", min: "-180", max: "180", className: "input is-small", name: "jemLonFrom" }),
                    hx("span", "〜"),
                    hx("input", { type: "number", step: "any", min: "-180", max: "180", className: "input is-small", name: "jemLonTo" }))),

            hx("div", { className: "field" },
                hx("label", { className: "label" }, "震源深さ [km]"),
                hx("div", { className: "control horizontal" },
                    hx("input", { type: "number", step: "1", className: "input is-small", name: "jemDepthFrom" }),
                    hx("span", "〜"),
                    hx("input", { type: "number", step: "1", className: "input is-small", name: "jemDepthTo" }))),

            hx("div", { className: "field" },
                hx("label", { className: "label" }, "気象庁マグニチュード"),
                hx("div", { className: "control horizontal" },
                    hx("input", { type: "number", step: "1", className: "input is-small", name: "mjmaFrom", value: "5" }),
                    hx("span", "〜"),
                    hx("input", { type: "number", step: "1", className: "input is-small", name: "mjmaTo" }))),

            hx("div", { className: "field field-eq-location-type-id" },
                hx("label", { className: "label" }, "地震発生位置種別"),
                hx("div", { className: "control horizontal" },
                    allEqLocationTypeIds
                        .map((x) => {
                            return hx("label", { className: "label" },
                                hx("input", { type: "checkbox", id: `eqLocationTypeId${x}`, value: "" + x, checked: true }),
                                eqLocationTypeIdToString(x));
                        }))),

            hx("div", { className: "field field-eq-location-type-id-source" },
                hx("label", { className: "label" }, "地震発生位置種別の評価方法"),
                hx("div", { className: "control horizontal" },
                    hx("label", { className: "label" },
                        hx("input", { type: "radio", name: "eqLocationTypeIdSources", value: "2,3" }),
                        "震源位置のみによる判定を除く"),
                    hx("label", { className: "label" },
                        hx("input", { type: "radio", name: "eqLocationTypeIdSources", value: "", checked: true }),
                        "全て"))),
        ];
    }

    #getParams() {
        const jemOriginTimeFrom = this.getDate("[name='jemOriginTimeFrom']", "floor");
        const jemOriginTimeTo = this.getDate("[name='jemOriginTimeTo']", "ceil");

        const jemLatFrom = this.getLat("[name='jemLatFrom']");
        const jemLatTo = this.getLat("[name='jemLatTo']");

        const jemLonFrom = this.getLon("[name='jemLonFrom']");
        const jemLonTo = this.getLon("[name='jemLonTo']");

        const jemDepthFrom = this.getNumber("[name='jemDepthFrom']");
        const jemDepthTo = this.getNumber("[name='jemDepthTo']");

        const mjmaFrom = this.getNumber("[name='mjmaFrom']");
        const mjmaTo = this.getNumber("[name='mjmaTo']");

        const eqLocationTypeId = this.getChecked("[id^='eqLocationTypeId']:checked", Number);
        const eqLocationTypeIdSource = this.getChecked("[name='eqLocationTypeIdSources']:checked", Number);

        return {
            jemOriginTimeFrom,
            jemOriginTimeTo,
            jemLatFrom,
            jemLatTo,
            jemLonFrom,
            jemLonTo,
            jemDepthFrom,
            jemDepthTo,
            mjmaFrom,
            mjmaTo,
            eqLocationTypeId,
            eqLocationTypeIdSource,
        };
    }

    /**
     * 地図上で選択された範囲を緯度経度の検索条件に設定する。
     *
     * @param extent 選択された範囲（Extent）
     */
    setExtent(extent: Extent) {
        const elem = this.getElem();
        const bottomLeft = getBottomLeft(extent);
        const topRight = getTopRight(extent);

        const jemLonFrom = pickElement<HTMLInputElement>("[name='jemLonFrom']", elem);
        const jemLatFrom = pickElement<HTMLInputElement>("[name='jemLatFrom']", elem);
        const jemLonTo = pickElement<HTMLInputElement>("[name='jemLonTo']", elem);
        const jemLatTo = pickElement<HTMLInputElement>("[name='jemLatTo']", elem);

        jemLonFrom.value = bottomLeft[0].toFixed(4);
        jemLatFrom.value = bottomLeft[1].toFixed(4);
        jemLonTo.value = topRight[0].toFixed(4);
        jemLatTo.value = topRight[1].toFixed(4);

        jemLonFrom.dispatchEvent(new Event("change", { bubbles: true }));
    }
}
